import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  BarChart3, TrendingUp, FileText, Download, Calendar, 
  Truck, Package, PoundSterling, Users, Clock, CheckCircle
} from 'lucide-react';
import api from '../services/api';

const ReportsPage = () => {
  const [activeTab, setActiveTab] = useState('operations');
  const [dateRange, setDateRange] = useState('30');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState({
    operations: {},
    commercial: {},
    finance: {}
  });

  useEffect(() => {
    fetchReportData();
  }, [dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      // Fetch data from various endpoints (no trailing slashes)
      const [ordersRes, deliveriesRes, invoicesRes, customersRes] = await Promise.all([
        api.get('/orders').catch(() => ({ data: [] })),
        api.get('/deliveries').catch(() => ({ data: [] })),
        api.get('/invoices').catch(() => ({ data: [] })),
        api.get('/customers').catch(() => ({ data: [] }))
      ]);

      const orders = Array.isArray(ordersRes.data) ? ordersRes.data : [];
      const deliveries = Array.isArray(deliveriesRes.data) ? deliveriesRes.data : [];
      const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
      const customers = Array.isArray(customersRes.data) ? customersRes.data : [];

      // Calculate Operations KPIs
      const completedDeliveries = deliveries.filter(d => d.status === 'completed');
      const totalDeliveries = deliveries.length;
      const otifRate = totalDeliveries > 0 ? (completedDeliveries.length / totalDeliveries * 100).toFixed(1) : 0;
      
      // Deliveries by status
      const deliveryStatusCounts = {
        completed: deliveries.filter(d => d.status === 'completed').length,
        pending: deliveries.filter(d => d.status === 'pending').length,
        in_progress: deliveries.filter(d => d.status === 'in_progress').length,
        failed: deliveries.filter(d => d.status === 'failed').length
      };

      // Orders by status
      const orderStatusCounts = {
        pending: orders.filter(o => o.status === 'pending').length,
        confirmed: orders.filter(o => o.status === 'confirmed').length,
        planned: orders.filter(o => o.status === 'planned').length,
        dispatched: orders.filter(o => o.status === 'dispatched').length,
        delivered: orders.filter(o => o.status === 'delivered').length,
        invoiced: orders.filter(o => o.status === 'invoiced').length
      };

      // Calculate Commercial data
      const totalRevenue = orders.reduce((sum, o) => sum + (o.grand_total || 0), 0);
      const avgOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;
      
      // Revenue by product (from order lines)
      const revenueByProduct = {};
      orders.forEach(order => {
        order.lines?.forEach(line => {
          const productName = line.product_name || 'Unknown';
          revenueByProduct[productName] = (revenueByProduct[productName] || 0) + (line.line_total || 0);
        });
      });

      // Calculate Finance data
      const totalInvoiced = invoices.reduce((sum, i) => sum + (i.grand_total || 0), 0);
      const totalPaid = invoices.reduce((sum, i) => sum + (i.paid_amount || 0), 0);
      const totalOutstanding = totalInvoiced - totalPaid;
      
      // Invoice status breakdown
      const invoiceStatusCounts = {
        paid: invoices.filter(i => i.status === 'paid').length,
        partial: invoices.filter(i => i.status === 'partial').length,
        overdue: invoices.filter(i => i.status === 'overdue').length,
        issued: invoices.filter(i => i.status === 'issued' || i.status === 'sent').length,
        draft: invoices.filter(i => i.status === 'draft').length
      };

      // Aged debt analysis
      const now = new Date();
      const agedDebt = {
        current: 0,
        days30: 0,
        days60: 0,
        days90: 0,
        over90: 0
      };
      
      invoices.forEach(inv => {
        if (inv.status === 'paid') return;
        const outstanding = (inv.grand_total || 0) - (inv.paid_amount || 0);
        if (outstanding <= 0) return;
        
        const dueDate = inv.due_date ? new Date(inv.due_date) : now;
        const daysOverdue = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
        
        if (daysOverdue <= 0) agedDebt.current += outstanding;
        else if (daysOverdue <= 30) agedDebt.days30 += outstanding;
        else if (daysOverdue <= 60) agedDebt.days60 += outstanding;
        else if (daysOverdue <= 90) agedDebt.days90 += outstanding;
        else agedDebt.over90 += outstanding;
      });

      // Customer credit exposure
      const creditExposure = customers.map(c => ({
        name: c.company_name?.substring(0, 20) || 'Unknown',
        limit: c.credit?.credit_limit || 0,
        exposure: c.credit?.current_exposure || 0,
        available: (c.credit?.credit_limit || 0) - (c.credit?.current_exposure || 0)
      })).sort((a, b) => b.exposure - a.exposure).slice(0, 10);

      setReportData({
        operations: {
          otifRate,
          totalDeliveries,
          completedDeliveries: completedDeliveries.length,
          deliveryStatusCounts,
          orderStatusCounts,
          totalOrders: orders.length
        },
        commercial: {
          totalRevenue,
          avgOrderValue,
          revenueByProduct,
          totalOrders: orders.length,
          totalCustomers: customers.length
        },
        finance: {
          totalInvoiced,
          totalPaid,
          totalOutstanding,
          invoiceStatusCounts,
          agedDebt,
          creditExposure,
          totalInvoices: invoices.length
        }
      });
    } catch (error) {
      console.error('Error fetching report data:', error);
    }
    setLoading(false);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value);
  };

  const COLORS = ['#f97316', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

  // Chart data transformations
  const deliveryStatusData = reportData.operations.deliveryStatusCounts ? 
    Object.entries(reportData.operations.deliveryStatusCounts).map(([name, value]) => ({ name, value })) : [];
  
  const orderStatusData = reportData.operations.orderStatusCounts ?
    Object.entries(reportData.operations.orderStatusCounts).map(([name, value]) => ({ name, value })) : [];

  const revenueByProductData = reportData.commercial.revenueByProduct ?
    Object.entries(reportData.commercial.revenueByProduct).map(([name, value]) => ({ 
      name: name.length > 15 ? name.substring(0, 15) + '...' : name, 
      revenue: value 
    })).sort((a, b) => b.revenue - a.revenue) : [];

  const agedDebtData = reportData.finance.agedDebt ? [
    { name: 'Current', value: reportData.finance.agedDebt.current },
    { name: '1-30 days', value: reportData.finance.agedDebt.days30 },
    { name: '31-60 days', value: reportData.finance.agedDebt.days60 },
    { name: '61-90 days', value: reportData.finance.agedDebt.days90 },
    { name: '90+ days', value: reportData.finance.agedDebt.over90 }
  ] : [];

  const invoiceStatusData = reportData.finance.invoiceStatusCounts ?
    Object.entries(reportData.finance.invoiceStatusCounts).map(([name, value]) => ({ name, value })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Reports</h1>
          <p className="text-muted-foreground">Business intelligence and analytics</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2">
            <Download className="w-4 h-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="operations" className="gap-2" data-testid="tab-operations">
            <Truck className="w-4 h-4" />
            Operations
          </TabsTrigger>
          <TabsTrigger value="commercial" className="gap-2" data-testid="tab-commercial">
            <TrendingUp className="w-4 h-4" />
            Commercial
          </TabsTrigger>
          <TabsTrigger value="finance" className="gap-2" data-testid="tab-finance">
            <PoundSterling className="w-4 h-4" />
            Finance
          </TabsTrigger>
        </TabsList>

        {/* Operations Tab */}
        <TabsContent value="operations" className="space-y-6 mt-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">OTIF Rate</p>
                    <p className="text-2xl font-bold">{reportData.operations.otifRate}%</p>
                    <p className="text-xs text-muted-foreground">On-Time In-Full</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Deliveries</p>
                    <p className="text-2xl font-bold">{reportData.operations.totalDeliveries}</p>
                    <p className="text-xs text-muted-foreground">{reportData.operations.completedDeliveries} completed</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Orders</p>
                    <p className="text-2xl font-bold">{reportData.operations.totalOrders}</p>
                    <p className="text-xs text-muted-foreground">All statuses</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Drops/Day</p>
                    <p className="text-2xl font-bold">{(reportData.operations.totalDeliveries / parseInt(dateRange)).toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">Per day average</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Delivery Status Breakdown</CardTitle>
                <CardDescription>Distribution of delivery statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={deliveryStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {deliveryStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Order Pipeline</CardTitle>
                <CardDescription>Orders by status in the pipeline</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={orderStatusData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip />
                      <Bar dataKey="value" fill="#f97316" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Commercial Tab */}
        <TabsContent value="commercial" className="space-y-6 mt-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Revenue</p>
                    <p className="text-2xl font-bold">{formatCurrency(reportData.commercial.totalRevenue)}</p>
                    <p className="text-xs text-muted-foreground">From all orders</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <PoundSterling className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Order Value</p>
                    <p className="text-2xl font-bold">{formatCurrency(reportData.commercial.avgOrderValue)}</p>
                    <p className="text-xs text-muted-foreground">Per order</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Orders</p>
                    <p className="text-2xl font-bold">{reportData.commercial.totalOrders}</p>
                    <p className="text-xs text-muted-foreground">Orders placed</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Package className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Active Customers</p>
                    <p className="text-2xl font-bold">{reportData.commercial.totalCustomers}</p>
                    <p className="text-xs text-muted-foreground">Total accounts</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Product Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Revenue by Product</CardTitle>
              <CardDescription>Sales breakdown by fuel type</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={revenueByProductData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Bar dataKey="revenue" fill="#f97316" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Finance Tab */}
        <TabsContent value="finance" className="space-y-6 mt-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Invoiced</p>
                    <p className="text-2xl font-bold">{formatCurrency(reportData.finance.totalInvoiced)}</p>
                    <p className="text-xs text-muted-foreground">{reportData.finance.totalInvoices} invoices</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Collected</p>
                    <p className="text-2xl font-bold">{formatCurrency(reportData.finance.totalPaid)}</p>
                    <p className="text-xs text-muted-foreground">Cash received</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Outstanding</p>
                    <p className="text-2xl font-bold text-orange-500">{formatCurrency(reportData.finance.totalOutstanding)}</p>
                    <p className="text-xs text-muted-foreground">To collect</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                    <PoundSterling className="w-5 h-5 text-orange-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="hover:border-orange-500/50 transition-colors">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Collection Rate</p>
                    <p className="text-2xl font-bold">
                      {reportData.finance.totalInvoiced > 0 
                        ? ((reportData.finance.totalPaid / reportData.finance.totalInvoiced) * 100).toFixed(1)
                        : 0}%
                    </p>
                    <p className="text-xs text-muted-foreground">Paid vs invoiced</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-purple-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Aged Debt Analysis</CardTitle>
                <CardDescription>Outstanding amounts by age</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={agedDebtData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(value) => `£${(value / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                        {agedDebtData.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={index < 2 ? '#10b981' : index < 4 ? '#f97316' : '#ef4444'} 
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Invoice Status</CardTitle>
                <CardDescription>Breakdown of invoice statuses</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={invoiceStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, value }) => `${name}: ${value}`}
                      >
                        {invoiceStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Credit Exposure Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Top 10 Credit Exposure</CardTitle>
              <CardDescription>Customers with highest credit utilization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 font-medium">Customer</th>
                      <th className="text-right py-3 px-4 font-medium">Credit Limit</th>
                      <th className="text-right py-3 px-4 font-medium">Current Exposure</th>
                      <th className="text-right py-3 px-4 font-medium">Available</th>
                      <th className="text-right py-3 px-4 font-medium">Utilization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.finance.creditExposure?.map((customer, idx) => {
                      const utilization = customer.limit > 0 ? (customer.exposure / customer.limit * 100) : 0;
                      return (
                        <tr key={idx} className="border-b last:border-0 hover:bg-muted/50">
                          <td className="py-3 px-4">{customer.name}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(customer.limit)}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(customer.exposure)}</td>
                          <td className="py-3 px-4 text-right">{formatCurrency(customer.available)}</td>
                          <td className="py-3 px-4 text-right">
                            <Badge variant={utilization > 80 ? "destructive" : utilization > 50 ? "warning" : "default"}>
                              {utilization.toFixed(0)}%
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
